import React, { useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { fr, enUS, es, pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const DF_LOCALES = { fr, en: enUS, es, pt };
const INTL_LOCALES = { fr: "fr-FR", en: "en-US", es: "es-ES", pt: "pt-PT" };

const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined;
};
const shiftYears = (d, years) => new Date(d.getFullYear() - years, d.getMonth(), d.getDate());

export const formatBirthDate = (ymd, lang) => {
  const d = parseYmd(ymd);
  return d ? new Intl.DateTimeFormat(INTL_LOCALES[lang] || INTL_LOCALES.fr, { day: "numeric", month: "long", year: "numeric" }).format(d) : "";
};

// Birth-date field rendered by Yalola (not the OS): the value is displayed in the
// app language, the calendar has month + year dropdowns (jump straight to a birth
// year) and only offers dates compatible with the age policy (min/max age).
export const BirthDatePicker = ({ value, onChange, lang = "fr", minAge = 0, maxAge = 0, placeholder, className, "data-testid": testId = "birthdate-picker" }) => {
  const [open, setOpen] = useState(false);
  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => shiftYears(today, minAge || 0), [today, minAge]);
  const minDate = useMemo(() => (maxAge > 0 ? shiftYears(today, maxAge) : new Date(1900, 0, 1)), [today, maxAge]);
  const selected = parseYmd(value);
  const label = formatBirthDate(value, lang);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn("flex h-10 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-transparent px-3 text-left text-base shadow-sm md:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring", className)}
          aria-label={placeholder}
          data-testid={testId}
          data-value={value || ""}
        >
          <span className={cn("flex-1 truncate", !label && "text-muted-foreground")} data-testid={`${testId}-label`}>{label || placeholder}</span>
          <CalendarDays size={16} className="shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" collisionPadding={8} data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          locale={DF_LOCALES[lang] || fr}
          selected={selected}
          defaultMonth={selected || maxDate}
          captionLayout="dropdown-buttons"
          fromDate={minDate}
          toDate={maxDate}
          disabled={{ before: minDate, after: maxDate }}
          onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }}
          classNames={{
            caption: "flex justify-center pt-1 relative items-center px-9",
            caption_label: "hidden",
            caption_dropdowns: "flex items-center gap-1",
            dropdown: "h-8 rounded-md border border-input bg-background px-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            dropdown_month: "relative",
            dropdown_year: "relative",
            vhidden: "sr-only",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default BirthDatePicker;
